import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Phone, ArrowRight, CheckCircle } from "lucide-react";

const STEPS = [
  {
    key: "P",
    title: "PARA",
    subtitle: "Detén lo que estás haciendo",
    color: "#6B9080",
    bg: "#EEF4F1",
    instruction: "Haz una pausa completa. El impulso que sientes es una señal neurológica, no una orden. Tu amígdala activó la alarma — no tienes que obedecerla.",
    action: "Cuenta conmigo hasta 5 y respira",
    type: "countdown",
  },
  {
    key: "A",
    title: "RESPIRA",
    subtitle: "Técnica 4-7-8",
    color: "#5B8FCC",
    bg: "#EDF4FF",
    instruction: "Esta técnica activa tu sistema nervioso parasimpático y calma la amígdala en segundos. Es ciencia, no magia.",
    action: "Haz 3 ciclos de respiración 4-7-8",
    type: "breathing",
  },
  {
    key: "U",
    title: "DISTRACTOR",
    subtitle: "Rompe el patrón neuronal",
    color: "#8B69CC",
    bg: "#F5F0FF",
    instruction: "Los cravings duran entre 15-30 minutos. Si cambias de actividad ahora, interrumpes el circuito neuronal del craving.",
    action: "Elige una actividad para los próximos 20 minutos",
    type: "distractor",
  },
  {
    key: "S",
    title: "APOYO",
    subtitle: "No estás solo/a",
    color: "#C47840",
    bg: "#FFF4EE",
    instruction: "La conexión humana libera oxitocina y bloquea la respuesta de estrés. Llamar a alguien es una de las estrategias más efectivas que existen.",
    action: "Contacta a alguien de tu red de apoyo",
    type: "support",
  },
  {
    key: "E",
    title: "EVALÚA",
    subtitle: "Tu corteza prefrontal habla",
    color: "#84A98C",
    bg: "#EEF4F1",
    instruction: "Ahora que tu amígdala está más calmada, tu corteza prefrontal puede pensar con claridad. Reflexiona sobre las consecuencias.",
    action: "¿Qué perderías si consumes ahora?",
    type: "evaluate",
  },
];

const DISTRACTORS = [
  "Salir a caminar 15 minutos",
  "Hacer 20 sentadillas o flexiones",
  "Llamar a un amigo/familiar",
  "Ver un video de humor",
  "Escuchar música que te guste",
  "Tomar una ducha fría",
  "Preparar algo de comer",
  "Escribir en el diario",
  "Meditar 10 minutos",
  "Hacer tareas del hogar",
];

function CountdownStep({ onComplete }) {
  const [count, setCount] = useState(5);
  useEffect(() => {
    if (count <= 0) { onComplete(); return; }
    const t = setTimeout(() => setCount(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count, onComplete]);
  return (
    <div className="flex flex-col items-center mt-8">
      <div className="w-32 h-32 rounded-full bg-[#6B9080] flex items-center justify-center shadow-lg">
        <span className="text-6xl font-bold text-white" style={{ fontFamily: "Manrope" }}>{count}</span>
      </div>
      <p className="text-[#2C4238] mt-4 text-center">Mantén la calma. El impulso pasará.</p>
    </div>
  );
}

function BreathingStep({ onComplete }) {
  const PHASES = [
    { label: "Inhala", duration: 4, scale: 1.4, color: "#6B9080" },
    { label: "Mantén", duration: 7, scale: 1.4, color: "#5B8FCC" },
    { label: "Exhala", duration: 8, scale: 1, color: "#84A98C" },
  ];
  const [phase, setPhase] = useState(0);
  const [count, setCount] = useState(4);
  const [cycles, setCycles] = useState(0);
  const [targetScale, setTargetScale] = useState(1);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isActive) return;
    if (count > 0) {
      const t = setTimeout(() => setCount(c => c - 1), 1000);
      return () => clearTimeout(t);
    } else {
      const nextPhase = (phase + 1) % PHASES.length;
      if (nextPhase === 0) {
        const newCycles = cycles + 1;
        setCycles(newCycles);
        if (newCycles >= 3) { setIsActive(false); onComplete(); return; }
      }
      setPhase(nextPhase);
      setTargetScale(PHASES[nextPhase].scale);
      setCount(PHASES[nextPhase].duration);
    }
  }, [isActive, count, phase, cycles]);

  const currentPhase = PHASES[phase];

  return (
    <div className="flex flex-col items-center mt-4">
      <p className="text-sm text-[#2C4238] mb-2">Ciclo {cycles + 1} de 3</p>
      <div
        className="w-32 h-32 rounded-full flex items-center justify-center shadow-lg cursor-pointer"
        style={{
          backgroundColor: currentPhase.color,
          transform: `scale(${isActive ? targetScale : 1})`,
          transition: `transform ${currentPhase.duration}s ease-in-out, background-color 1s ease`,
        }}
        onClick={() => !isActive && (setIsActive(true), setTargetScale(PHASES[0].scale))}
      >
        <div className="text-center text-white">
          <p className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>{isActive ? count : "Iniciar"}</p>
          {isActive && <p className="text-xs mt-1">{currentPhase.label}</p>}
        </div>
      </div>
      <p className="text-lg font-semibold text-[#1E2F23] mt-4" style={{ fontFamily: "Manrope" }}>
        {isActive ? currentPhase.label : "Toca para comenzar"}
      </p>
      <p className="text-sm text-[#2C4238] text-center mt-1">
        Inhala 4s → Mantén 7s → Exhala 8s
      </p>
      {!isActive && cycles === 0 && (
        <p className="text-xs text-[#4A6E5C] mt-2">3 ciclos completos</p>
      )}
    </div>
  );
}

function DistractorStep({ onComplete }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="mt-4">
      <div className="grid grid-cols-1 gap-2">
        {DISTRACTORS.map((d, i) => (
          <button
            key={i}
            data-testid={`distractor-${i}`}
            onClick={() => setSelected(i)}
            className={`p-3 rounded-2xl text-left text-sm font-medium transition-all duration-200 border ${
              selected === i
                ? "bg-[#8B69CC] text-white border-[#8B69CC]"
                : "bg-white text-[#1E2F23] border-[#E4EBE6] hover:border-[#8B69CC]"
            }`}
          >
            {d}
          </button>
        ))}
      </div>
      {selected !== null && (
        <button
          data-testid="distractor-confirm"
          onClick={onComplete}
          className="w-full mt-4 py-4 rounded-2xl bg-[#8B69CC] text-white font-semibold flex items-center justify-center gap-2 transition-all"
        >
          <CheckCircle size={18} /> Lo estoy haciendo
        </button>
      )}
    </div>
  );
}

function SupportStep({ onComplete }) {
  const contacts = JSON.parse(localStorage.getItem("reconectados_contacts") || "[]");
  const crisisLines = [
    { name: "Emergencias España", number: "112" },
    { name: "SAMUR Social Madrid", number: "900 22 35 22" },
    { name: "CONADIC México", number: "800 911 2000" },
    { name: "Emergencias México", number: "911" },
    { name: "CEDRO Perú", number: "01 440 0676" },
  ];

  return (
    <div className="mt-4 space-y-4">
      {contacts.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-[#1E2F23] mb-2" style={{ fontFamily: "Manrope" }}>Mis contactos</p>
          {contacts.map((c) => (
            <a
              key={c.id}
              href={`tel:${c.phone}`}
              data-testid={`call-contact-${c.id}`}
              className="flex items-center justify-between bg-white rounded-2xl p-4 border border-[#E4EBE6] mb-2 hover:border-[#C47840] transition-colors"
            >
              <div>
                <p className="font-semibold text-sm text-[#1E2F23]">{c.name}</p>
                <p className="text-xs text-[#2C4238]">{c.relationship} · {c.phone}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-[#FFF4EE] flex items-center justify-center">
                <Phone size={18} className="text-[#C47840]" />
              </div>
            </a>
          ))}
        </div>
      )}
      <div>
        <p className="text-sm font-semibold text-[#1E2F23] mb-2" style={{ fontFamily: "Manrope" }}>Líneas de crisis</p>
        {crisisLines.map((line) => (
          <a
            key={line.number}
            href={`tel:${line.number}`}
            data-testid={`crisis-line-${line.number}`}
            className="flex items-center justify-between bg-white rounded-2xl p-4 border border-[#E4EBE6] mb-2"
          >
            <div>
              <p className="font-semibold text-sm text-[#1E2F23]">{line.name}</p>
              <p className="text-xs text-[#2C4238]">{line.number}</p>
            </div>
            <Phone size={18} className="text-[#6B9080]" />
          </a>
        ))}
      </div>
      <button
        data-testid="support-continue"
        onClick={onComplete}
        className="w-full py-4 rounded-2xl bg-[#C47840] text-white font-semibold flex items-center justify-center gap-2"
      >
        He contactado a alguien <CheckCircle size={18} />
      </button>
    </div>
  );
}

function EvaluateStep({ onComplete }) {
  const [text, setText] = useState("");
  return (
    <div className="mt-4">
      <p className="text-sm text-[#2C4238] mb-3 leading-relaxed">
        Tu corteza prefrontal está más activa ahora. Escribe qué perderías si consumes:
      </p>
      <textarea
        data-testid="evaluate-textarea"
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Mi salud, mi familia, mi trabajo, mi recuperación..."
        className="w-full p-4 rounded-2xl border border-[#E4EBE6] bg-white text-sm text-[#1E2F23] resize-none h-36 focus:outline-none focus:border-[#6B9080]"
      />
      <button
        data-testid="evaluate-complete"
        onClick={onComplete}
        className="w-full mt-4 py-4 rounded-2xl bg-[#6B9080] text-white font-semibold flex items-center justify-center gap-2"
      >
        <CheckCircle size={18} /> He superado el impulso
      </button>
    </div>
  );
}

export default function CrisisMode() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  const currentStep = STEPS[step];

  const handleComplete = () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      setCompleted(true);
    }
  };

  if (completed) {
    return (
      <div className="crisis-overlay flex flex-col items-center justify-center p-8 text-center">
        <div className="w-24 h-24 rounded-full bg-[#EEF4F1] flex items-center justify-center mb-6 animate-fade-in">
          <CheckCircle size={48} className="text-[#6B9080]" />
        </div>
        <h2 className="text-3xl font-bold text-[#1E2F23] mb-3 animate-fade-in delay-100" style={{ fontFamily: "Manrope" }}>
          Lo lograste
        </h2>
        <p className="text-[#2C4238] leading-relaxed mb-2 animate-fade-in delay-200">
          Completaste el Plan PAUSE. Tu corteza prefrontal acaba de demostrar que puede contra el craving.
        </p>
        <p className="text-sm text-[#6B9080] font-medium mb-8 animate-fade-in delay-300">
          Cada vez que lo logras, el circuito del autocontrol se fortalece.
        </p>
        <button
          data-testid="crisis-complete-home"
          onClick={() => navigate("/")}
          className="px-8 py-4 bg-[#6B9080] text-white rounded-full font-semibold text-lg animate-fade-in delay-400"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  return (
    <div className="crisis-overlay">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-[#E4EBE6]">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: currentStep.color, fontFamily: "Manrope" }}
          >
            {currentStep.key}
          </div>
          <div>
            <p className="font-bold text-[#1E2F23] text-lg" style={{ fontFamily: "Manrope" }}>{currentStep.title}</p>
            <p className="text-xs text-[#2C4238]">{currentStep.subtitle}</p>
          </div>
        </div>
        <button
          data-testid="crisis-close"
          onClick={() => navigate("/")}
          className="w-10 h-10 rounded-full bg-[#F7F9F7] flex items-center justify-center text-[#2C4238] hover:bg-[#E4EBE6] transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Progress */}
      <div className="flex gap-1.5 px-5 pt-4">
        {STEPS.map((s, i) => (
          <div
            key={s.key}
            className="flex-1 h-1.5 rounded-full transition-all duration-300"
            style={{ backgroundColor: i <= step ? currentStep.color : "#E4EBE6" }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="px-5 pt-4 pb-32 overflow-y-auto">
        <div className={`p-4 rounded-2xl mb-4`} style={{ backgroundColor: currentStep.bg }}>
          <p className="text-sm text-[#1E2F23] leading-relaxed">{currentStep.instruction}</p>
        </div>
        <p className="text-sm font-semibold text-[#1E2F23] mb-2" style={{ fontFamily: "Manrope" }}>
          {currentStep.action}
        </p>

        {currentStep.type === "countdown" && <CountdownStep onComplete={() => setStep(1)} />}
        {currentStep.type === "breathing" && <BreathingStep onComplete={handleComplete} />}
        {currentStep.type === "distractor" && <DistractorStep onComplete={handleComplete} />}
        {currentStep.type === "support" && <SupportStep onComplete={handleComplete} />}
        {currentStep.type === "evaluate" && <EvaluateStep onComplete={handleComplete} />}

        {/* Skip for breathing/countdown after showing */}
        {(currentStep.type === "breathing" || currentStep.type === "countdown") && (
          <button
            data-testid="skip-step"
            onClick={handleComplete}
            className="w-full mt-6 py-3 rounded-2xl text-[#2C4238] text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#F0F4F1] transition-colors"
          >
            Siguiente paso <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
